import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import {
    Box,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../hooks/use.debounce";
import { getFormatedDate } from "../../utils/common";
import { CustomSearchInput, FORM_SELECTFIELD_STYLES, START_ADORNMENT_FOR_CUSTOM_SEARCH_INPUT, TABLE_BODY_ROW_MUI_STYLES, TABLE_HEADER_ROW_MUI_STYLES } from "../../utils/constant";
import { disableInfluencerSubscription, getAllInfluencerSubscriptions, getAllInfluencerSubscriptionTypes } from "./action";
import { updateSubscription } from "./subscriptionSlice";
import CustomPagination from "../../utils/CustomPagination";
import { ROUTES } from "../../utils/routes";

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort, isSticky } = props;
    const createSortHandler = (property) => (event) => {
        if (property === "action") {
            return;
        }
        onRequestSort(event, property);
    };

    const headCells = [
        { id: "sno", numeric: false, disablePadding: true, label: "SI No.", type: "" },
        { id: "influencerName", numeric: false, disablePadding: true, label: "Influencer Name", type: "" },
        { id: "type", numeric: false, disablePadding: true, label: "Plan", type: "" },
        { id: "startDate", numeric: false, disablePadding: true, label: "Start Date", type: "" },
        { id: "endDate", numeric: false, disablePadding: true, label: "End Date", type: "" },
        { id: "action", numeric: false, disablePadding: true, label: "Actions", type: "" },
    ];

    return (
        <TableHead className={`hideActionSort ${isSticky} `}>
            <TableRow sx={TABLE_HEADER_ROW_MUI_STYLES}>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell?.id}
                        align={headCell.numeric ? "right" : "left"}
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            IconComponent={null}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

export default function InfluencerSubscriptionList(props) {
    const navigate = useNavigate();
    const states = useSelector((state) => state.app);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    const [sticky, setSticky] = useState("");
    const [subscriptionTypes, setSubscriptionTypes] = useState([]);
    const dispatch = useDispatch();
    const {
        limit,
        page,
        sort_key,
        totalPages,
        filters,
        influencerListing,
        influencerTotalCount,
    } = useSelector((state) => state.subscriptionSlice);

    const debouncedSearch = useDebounce(filters.name, 500);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
        dispatch(updateSubscription({ sort_type: isAsc ? "asc" : "desc", sort_key: property }));
    };

    // Skips the mount run, which would otherwise duplicate the fetch in the mount effect below
    // (and fire before it resets the page index).
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (debouncedSearch || filters.name == "")
            dispatch(getAllInfluencerSubscriptions())
    }, [debouncedSearch]);

    useEffect(() => {
        // page/limit are shared with the admin subscriptions table via subscriptionSlice,
        // so clear any page index carried over from there before the first fetch.
        dispatch(updateSubscription({ page: 0 }));
        dispatch(getAllInfluencerSubscriptions());
        dispatch(getAllInfluencerSubscriptionTypes(setSubscriptionTypes));
    }, []);

    const handleChange = (value) => {
        dispatch(updateSubscription({ page: value - 1 }));
        dispatch(getAllInfluencerSubscriptions());
    };

    const handleSearch = (event) => {
        dispatch(updateSubscription({ filters: { ...filters, name: event.target.value }, page: 0 }));
    };

    const handleSearchClear = () => {
        dispatch(updateSubscription({ filters: { ...filters, name: "" }, page: 0 }));
        dispatch(getAllInfluencerSubscriptions());
    };

    const handleChangePerPage = (event, value) => {
        dispatch(updateSubscription({ limit: event.target.value, page: 0 }));
        dispatch(getAllInfluencerSubscriptions());
    };

    const tableRef = useRef();

    return (
        <>
            <Stack direction="row" alignItems="center" mb={4}>
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    flexWrap="wrap" gap={2}
                    sx={{ minHeight: '42px', paddingX: '16px', width: '100%' }}
                >
                    <Typography className="page-title" variant="h1">Influencer List with Plan</Typography>
                    <Stack direction="row" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={2}>
                        <CustomSearchInput
                            className="inputSearch sContrl p-0"
                            value={filters.name}
                            placeholder={"Search here..."}
                            handleSearch={handleSearch}
                            clearSearch={handleSearchClear}
                            startAdornment={START_ADORNMENT_FOR_CUSTOM_SEARCH_INPUT}
                        />

                        <Select
                            sx={{ ...FORM_SELECTFIELD_STYLES, maxHeight: '48px', maxWidth: '200px', width: '200px' }}
                            MenuProps={{ PaperProps: { sx: { borderRadius: "8px", mt: 1, }, }, }}
                            value={filters?.type || ''}
                            name="status"
                            onChange={(e) => {
                                dispatch(updateSubscription({ filters: { ...filters, type: e.target.value }, page: 0 }));
                                dispatch(getAllInfluencerSubscriptions());
                            }}
                            displayEmpty
                            defaultValue={""}
                            justifyContent={"flex-end"}
                            className="upperCase"
                        >
                            <MenuItem disabled value="" hidden>Type of Product</MenuItem>
                            <MenuItem value="" >All</MenuItem>
                            {subscriptionTypes.map((v) => <MenuItem className="upperCase" key={v.type} value={v.type} >{v.type}</MenuItem>)}
                            <MenuItem value="custom" >Custom</MenuItem>
                        </Select>

                        <button className="btnPrimary" onClick={() => navigate(ROUTES.ADD_INFLUENCER_SUBSCRIPTION)}>
                            Assign Plan
                        </button>
                        <Tooltip title="Refresh listing">
                            <RotateLeftIcon toolTip="Refresh page" size="small" sx={{ cursor: "pointer", color: '#6C7692', mt: 1.5 }}
                                onClick={() => {
                                    dispatch(updateSubscription({ filters: {}, page: 0 }))
                                    dispatch(getAllInfluencerSubscriptions());
                                }} />
                        </Tooltip>

                    </Stack>
                </Stack>
            </Stack>

            <Stack direction={"column"} gap={2}>
                <Box sx={{
                    backgroundColor: '#fff',
                    transition: 'all .5s ease-in-out',
                    position: 'relative',
                    border: '1px solid rgba(170, 170, 170, 0.3)',
                }}>
                    <TableContainer ref={tableRef}>
                        <Table aria-label="sticky table">
                            <EnhancedTableHead
                                isSticky={sticky}
                                order={order}
                                orderBy={sort_key}
                                onRequestSort={handleRequestSort}
                            />
                            <TableBody>
                                {influencerListing && influencerListing?.length ? (
                                    influencerListing?.map((row, index) => {
                                        return (
                                            <TableRow hover role="checkbox" tabIndex={-1} key={row?.id} sx={TABLE_BODY_ROW_MUI_STYLES}>
                                                <TableCell component="td" scope="row">{(page * limit) + index + 1}</TableCell>
                                                <TableCell component="td" scope="row">{row?.influencerName || "N/A"}</TableCell>
                                                <TableCell component="td" scope="row">{row?.type || "N/A"}</TableCell>
                                                <TableCell component="td" scope="row">{row?.startDate ? getFormatedDate(row?.startDate, "DD MMMM YYYY") : "N/A"}</TableCell>
                                                <TableCell component="td" scope="row">{row?.endDate ? getFormatedDate(row?.endDate, "DD MMMM YYYY") : "N/A"}</TableCell>
                                                <TableCell sx={{ cursor: "pointer" }}>
                                                    <Stack direction={"row"} sx={{ gap: '8px' }}>
                                                        <div className="actBtnGrps">
                                                            <span className="viewIcon" onClick={() => navigate(`${ROUTES.DETAILS_INFLUENCER_SUBSCRIPTION}/${row.id}`)}>View</span>
                                                            {row?.isActive === 1 && (
                                                                <span className="deleteIcon" onClick={() => {
                                                                    if (window.confirm("Are you sure you want to disable this subscription?")) {
                                                                        dispatch(disableInfluencerSubscription(row.id));
                                                                    }
                                                                }}>Disable</span>
                                                            )}
                                                        </div>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow colSpan={6}>
                                        <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                                            <Typography color="inherit" variant="subtitle1" component="h3">No Data found!</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Stack sx={{ background: 'transparent', height: '60px', border: '1px solid #E9EAEB' }} direction="row" alignItems={"center"} justifyContent={"flex-end"}>
                        <CustomPagination
                            count={Math.ceil(totalPages / limit) || 0}
                            onChange={handleChange}
                            currentPage={page}
                            handleChangePerPage={handleChangePerPage}
                            limit={limit}
                            totalCount={influencerTotalCount}
                            listingCount={influencerListing?.length}
                            defaultPage={1} siblingCount={1} boundaryCount={1} />
                    </Stack>
                </Box>
            </Stack>
        </>
    );
}
